/**
 * Optional crop/trim step: lets a user cut a bystander, jewelry, or
 * background out of a photo before it becomes part of the comparison.
 *
 * This is deliberately separate from the compare view's pan/zoom. Panning
 * only picks which window of the *whole* photo shows through a fixed-aspect
 * frame — if an unwanted person or object sits right next to the subject,
 * no pan/zoom combination can exclude it without also cutting off the
 * subject. A real crop discards those pixels from the source entirely, so
 * downstream pan/zoom never has to work around them.
 *
 * Runs over a static overlay already in index.html (#cropStage) rather than
 * building DOM at call time, matching the pattern used for the fullscreen
 * export stage.
 */
import { clamp } from './geometry';

export interface CropRectPx {
  x: number;
  y: number;
  w: number;
  h: number;
}

type Handle = 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

/** Crop rect may not shrink below this fraction of the source's shorter side. */
const MIN_FRACTION = 0.1;
/** How close (in displayed px) a pointer must be to a handle to grab it. */
const HANDLE_RADIUS = 20;
/** Visual size of the corner/edge handle markers. */
const HANDLE_SIZE = 14;
/** Margin around the canvas inside the crop card, in CSS px, both axes. */
const STAGE_PADDING = 24;

export interface CropElements {
  stage: HTMLElement;
  canvas: HTMLCanvasElement;
  resetBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  applyBtn: HTMLButtonElement;
}

/**
 * Opens the crop overlay for `source` and resolves with a new canvas
 * containing just the selected region, or `null` if the user cancels.
 * Only one crop session runs at a time (the caller owns that invariant by
 * disabling the crop buttons while the stage is open).
 */
export function openCropEditor(source: HTMLCanvasElement, els: CropElements): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const { stage, canvas, resetBtn, cancelBtn, applyBtn } = els;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    const iw = source.width;
    const ih = source.height;
    const minW = Math.max(20, Math.round(Math.min(iw, ih) * MIN_FRACTION));
    const minH = minW;

    let rect: CropRectPx = { x: 0, y: 0, w: iw, h: ih };
    let scale = 1;
    let dragging: Handle | null = null;
    let dragStart: { x: number; y: number; rect: CropRectPx } | null = null;

    function layout(): void {
      const maxW = Math.max(160, window.innerWidth - STAGE_PADDING * 2);
      const maxH = Math.max(160, window.innerHeight - 220); // leaves room for hint + buttons
      scale = Math.min(1, maxW / iw, maxH / ih);
      canvas.width = Math.max(1, Math.round(iw * scale));
      canvas.height = Math.max(1, Math.round(ih * scale));
      render();
    }

    function toDisplay(r: CropRectPx) {
      return { x: r.x * scale, y: r.y * scale, w: r.w * scale, h: r.h * scale };
    }

    function render(): void {
      const w = canvas.width;
      const h = canvas.height;
      ctx!.clearRect(0, 0, w, h);
      ctx!.drawImage(source, 0, 0, w, h);

      const d = toDisplay(rect);
      // Dim everything outside the selection.
      ctx!.fillStyle = 'rgba(0,0,0,0.55)';
      ctx!.fillRect(0, 0, w, d.y); // top
      ctx!.fillRect(0, d.y + d.h, w, h - d.y - d.h); // bottom
      ctx!.fillRect(0, d.y, d.x, d.h); // left
      ctx!.fillRect(d.x + d.w, d.y, w - d.x - d.w, d.h); // right

      // Selection border + thirds guide.
      ctx!.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx!.lineWidth = 1.5;
      ctx!.strokeRect(d.x + 0.75, d.y + 0.75, d.w - 1.5, d.h - 1.5);
      ctx!.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx!.beginPath();
      for (const f of [1 / 3, 2 / 3]) {
        ctx!.moveTo(d.x + d.w * f, d.y);
        ctx!.lineTo(d.x + d.w * f, d.y + d.h);
        ctx!.moveTo(d.x, d.y + d.h * f);
        ctx!.lineTo(d.x + d.w, d.y + d.h * f);
      }
      ctx!.stroke();

      // Handles: corners (resize both axes) + edge midpoints (resize one axis).
      const points: [number, number, Handle][] = [
        [d.x, d.y, 'nw'],
        [d.x + d.w / 2, d.y, 'n'],
        [d.x + d.w, d.y, 'ne'],
        [d.x, d.y + d.h / 2, 'w'],
        [d.x + d.w, d.y + d.h / 2, 'e'],
        [d.x, d.y + d.h, 'sw'],
        [d.x + d.w / 2, d.y + d.h, 's'],
        [d.x + d.w, d.y + d.h, 'se'],
      ];
      ctx!.fillStyle = '#fff';
      ctx!.strokeStyle = 'rgba(30,27,22,0.4)';
      ctx!.lineWidth = 1;
      for (const [x, y] of points) {
        ctx!.fillRect(x - HANDLE_SIZE / 2, y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        ctx!.strokeRect(x - HANDLE_SIZE / 2, y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      }

      applyBtn.disabled = false;
    }

    function pointFromEvent(e: MouseEvent | Touch): { x: number; y: number } {
      const b = canvas.getBoundingClientRect();
      return { x: e.clientX - b.left, y: e.clientY - b.top };
    }

    function hitTest(p: { x: number; y: number }): Handle | null {
      const d = toDisplay(rect);
      const near = (hx: number, hy: number): boolean => Math.hypot(p.x - hx, p.y - hy) <= HANDLE_RADIUS;
      const corners: [number, number, Handle][] = [
        [d.x, d.y, 'nw'],
        [d.x + d.w, d.y, 'ne'],
        [d.x, d.y + d.h, 'sw'],
        [d.x + d.w, d.y + d.h, 'se'],
      ];
      for (const [hx, hy, h] of corners) if (near(hx, hy)) return h;
      const edges: [number, number, Handle][] = [
        [d.x + d.w / 2, d.y, 'n'],
        [d.x + d.w / 2, d.y + d.h, 's'],
        [d.x, d.y + d.h / 2, 'w'],
        [d.x + d.w, d.y + d.h / 2, 'e'],
      ];
      for (const [hx, hy, h] of edges) if (near(hx, hy)) return h;
      if (p.x >= d.x && p.x <= d.x + d.w && p.y >= d.y && p.y <= d.y + d.h) return 'move';
      return null;
    }

    function applyDrag(p: { x: number; y: number }): void {
      if (!dragging || !dragStart) return;
      const dx = (p.x - dragStart.x) / scale;
      const dy = (p.y - dragStart.y) / scale;
      const s = dragStart.rect;
      let { x, y, w, h } = s;

      if (dragging === 'move') {
        x = clamp(s.x + dx, 0, iw - s.w);
        y = clamp(s.y + dy, 0, ih - s.h);
      } else {
        let left = s.x;
        let top = s.y;
        let right = s.x + s.w;
        let bottom = s.y + s.h;
        if (dragging.includes('w')) left = clamp(s.x + dx, 0, right - minW);
        if (dragging.includes('e')) right = clamp(right + dx, left + minW, iw);
        if (dragging.includes('n')) top = clamp(s.y + dy, 0, bottom - minH);
        if (dragging.includes('s')) bottom = clamp(bottom + dy, top + minH, ih);
        x = left;
        y = top;
        w = right - left;
        h = bottom - top;
      }
      rect = { x, y, w, h };
      render();
    }

    function onDown(p: { x: number; y: number }): void {
      const h = hitTest(p);
      if (!h) return;
      dragging = h;
      dragStart = { x: p.x, y: p.y, rect: { ...rect } };
    }
    function onMove(p: { x: number; y: number }): void {
      if (dragging) applyDrag(p);
    }
    function onUp(): void {
      dragging = null;
      dragStart = null;
    }

    const mouseDown = (e: MouseEvent) => onDown(pointFromEvent(e));
    const mouseMove = (e: MouseEvent) => onMove(pointFromEvent(e));
    const mouseUp = () => onUp();
    const touchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      onDown(pointFromEvent(e.touches[0]!));
    };
    const touchMove = (e: TouchEvent) => {
      if (!dragging || e.touches.length !== 1) return;
      e.preventDefault();
      onMove(pointFromEvent(e.touches[0]!));
    };
    const touchEnd = () => onUp();
    const onResize = () => layout();

    function cleanup(): void {
      canvas.removeEventListener('mousedown', mouseDown);
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('mouseup', mouseUp);
      canvas.removeEventListener('touchstart', touchStart);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('touchcancel', touchEnd);
      window.removeEventListener('resize', onResize);
      resetBtn.removeEventListener('click', onReset);
      cancelBtn.removeEventListener('click', onCancel);
      applyBtn.removeEventListener('click', onApply);
      stage.hidden = true;
    }

    function onReset(): void {
      rect = { x: 0, y: 0, w: iw, h: ih };
      render();
    }
    function onCancel(): void {
      cleanup();
      resolve(null);
    }
    function onApply(): void {
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round(rect.w));
      out.height = Math.max(1, Math.round(rect.h));
      const octx = out.getContext('2d');
      if (!octx) {
        cleanup();
        resolve(null);
        return;
      }
      octx.drawImage(source, rect.x, rect.y, rect.w, rect.h, 0, 0, out.width, out.height);
      cleanup();
      resolve(out);
    }

    canvas.addEventListener('mousedown', mouseDown);
    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('mouseup', mouseUp);
    canvas.addEventListener('touchstart', touchStart, { passive: false });
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd);
    window.addEventListener('touchcancel', touchEnd);
    window.addEventListener('resize', onResize);
    resetBtn.addEventListener('click', onReset);
    cancelBtn.addEventListener('click', onCancel);
    applyBtn.addEventListener('click', onApply);

    stage.hidden = false;
    layout();
  });
}
